import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

interface Hook {
  id: string;
  isSystem: boolean;
  title: string;
  text: string;
  language: 'uk' | 'ru' | 'pl' | 'lv' | 'en' | null;
  platform: string | null;
  niche: string | null;
  sourceUrl: string | null;
  usageCount: number;
}

export default function HookLibrary() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [language, setLanguage] = useState<'all' | 'ru' | 'uk' | 'pl' | 'lv' | 'en' | 'lv' | 'en'>('all');
  const [niche, setNiche] = useState('');
  const [busy, setBusy] = useState(false);

  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [newLang, setNewLang] = useState<'ru' | 'uk' | 'pl' | 'lv' | 'en'>('ru');
  const [newNiche, setNewNiche] = useState('');
  const [newPlatform, setNewPlatform] = useState('');

  const [searchNiche, setSearchNiche] = useState('');
  const [searchLang, setSearchLang] = useState<'ru' | 'uk' | 'pl' | 'lv' | 'en'>('ru');

  const load = () => {
    const params = new URLSearchParams();
    if (language !== 'all') params.set('language', language);
    if (niche) params.set('niche', niche);
    return api.get(`/hooks?${params}`).then((r) => setHooks(r.data.hooks)).catch(() => {});
  };

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, language, niche]);

  async function addHook(e: FormEvent) {
    e.preventDefault();
    if (!newTitle || !newText) return;
    setBusy(true);
    try {
      await api.post('/hooks', {
        title: newTitle, text: newText, language: newLang,
        niche: newNiche || null, platform: newPlatform || null,
      });
      setNewTitle(''); setNewText(''); setNewNiche(''); setNewPlatform('');
      toast.success('Хук добавлен');
      load();
    } finally { setBusy(false); }
  }

  async function deleteHook(id: string) {
    if (!confirm('Удалить хук?')) return;
    try { await api.delete(`/hooks/${id}`); load(); }
    catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    }
  }

  async function searchCompetitors() {
    if (!searchNiche) return;
    setBusy(true);
    try {
      const r = await api.post('/hooks/search-competitors', { niche: searchNiche, language: searchLang, count: 10 });
      toast.success(`Найдено: ${r.data.saved} хуков`);
      setSearchNiche('');
      load();
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Библиотека хуков</h1>
          <p className="text-slate-500 mt-1.5">Цепляющие первые фразы. AI вставляет их в начало скриптов.</p>
        </header>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <form onSubmit={addHook} className="card-glass p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-700">✏️ Добавить вручную</h2>
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Короткое имя" className="input" required />
            <textarea value={newText} onChange={(e) => setNewText(e.target.value)} rows={3}
              placeholder="Текст hook (1-2 предложения)" className="textarea" required />
            <div className="flex gap-2 flex-wrap">
              <select value={newLang} onChange={(e) => setNewLang(e.target.value as 'ru' | 'uk' | 'pl' | 'lv' | 'en')} className="select">
                <option value="ru">🇷🇺 ru</option>
                <option value="uk">🇺🇦 uk</option>
                <option value="pl">🇵🇱 pl</option>
                <option value="lv">🇱🇻 lv</option>
                <option value="en">🇬🇧 en</option>
              </select>
              <input value={newNiche} onChange={(e) => setNewNiche(e.target.value)} placeholder="ниша" className="input flex-1" />
              <input value={newPlatform} onChange={(e) => setNewPlatform(e.target.value)} placeholder="платформа" className="input flex-1" />
            </div>
            <button type="submit" disabled={busy} className="btn btn-md btn-primary w-full">+ Добавить хук</button>
          </form>

          <div className="card-glass p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-700">🔍 Хуки конкурентов</h2>
            <p className="text-xs text-slate-500">Brave ищет → AI извлекает hook-фразы. Нужен Brave Search ключ.</p>
            <input value={searchNiche} onChange={(e) => setSearchNiche(e.target.value)}
              placeholder='Например: "AI для малого бизнеса"' className="input" />
            <select value={searchLang} onChange={(e) => setSearchLang(e.target.value as 'ru' | 'uk' | 'pl' | 'lv' | 'en')} className="select">
              <option value="ru">🇷🇺 ru</option>
              <option value="uk">🇺🇦 uk</option>
              <option value="pl">🇵🇱 pl</option>
            </select>
            <button onClick={searchCompetitors} disabled={busy || !searchNiche} className="btn btn-md btn-success w-full">
              🔎 Запустить поиск
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <label className="text-sm text-slate-600">Язык:</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value as typeof language)} className="select">
            <option value="all">все</option>
            <option value="ru">ru</option>
            <option value="uk">uk</option>
            <option value="pl">pl</option>
            <option value="lv">lv</option>
            <option value="en">en</option>
          </select>
          <input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="фильтр по нише" className="input w-auto flex-1 max-w-xs" />
          <span className="ml-auto text-sm text-slate-500">всего: {hooks.length}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {hooks.length === 0 && (
            <div className="col-span-full card-glass p-12 text-center">
              <div className="text-5xl mb-3">🎯</div>
              <p className="text-sm text-slate-500">Нет хуков под фильтрами.</p>
            </div>
          )}
          {hooks.map((h) => (
            <div key={h.id} className="card-glass card-glass-hover p-4">
              <div className="flex items-start justify-between mb-2 gap-3">
                <div>
                  <div className="font-semibold text-sm">{h.title}</div>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {h.language && <span className="badge badge-slate">{h.language}</span>}
                    {h.niche && <span className="badge badge-indigo">{h.niche}</span>}
                    {h.platform && <span className="badge badge-violet">{h.platform}</span>}
                    {h.isSystem && <span className="badge badge-amber">system</span>}
                  </div>
                </div>
                {!h.isSystem && (
                  <button onClick={() => deleteHook(h.id)} className="text-slate-400 hover:text-rose-600 text-sm">🗑</button>
                )}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{h.text}</p>
              {h.sourceUrl && (
                <a href={h.sourceUrl} target="_blank" rel="noreferrer"
                   className="text-xs text-indigo-600 hover:underline mt-2 block truncate">{h.sourceUrl}</a>
              )}
              <div className="text-xs text-slate-400 mt-2">Использован {h.usageCount} раз</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
